// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { TimeRecordFilter } from '@/costs/api/get-time-records';
import { timeRecordAggregateSchema } from '@/costs/api/types';
import { z } from 'zod';

/**
 * Response schema for time record aggregate API
 */
const timeRecordAggregateResponseSchema = z.object({
  data: timeRecordAggregateSchema,
  metadata: responseMetadataSchema,
});

/**
 * Fetches time record aggregates based on the provided filters.
 * Uses the same filter interface as the time records endpoint.
 */
export async function getTimeRecordAggregate(
  filter?: Omit<TimeRecordFilter, 'pageSize' | 'pageNumber' | 'sort'>
) {
  const params = new URLSearchParams();

  if (filter?.search) {
    params.append('search', filter.search);
  }
  if (filter?.startDate) {
    params.append('startDate', filter.startDate);
  }
  if (filter?.endDate) {
    params.append('endDate', filter.endDate);
  }
  if (filter?.personIds && filter.personIds.length > 0) {
    filter.personIds.forEach((id) => {
      params.append('workerId', id);
    });
  }
  if (filter?.projectIds && filter.projectIds.length > 0) {
    filter.projectIds.forEach((id) => {
      params.append('projectId', id);
    });
  }
  if (filter?.categories && filter.categories.length > 0) {
    filter.categories.forEach((category) => {
      params.append('category', category);
    });
  }

  const url = params.toString()
    ? `/v1/time-record-aggregate/summary?${params.toString()}`
    : '/v1/time-record-aggregate/summary';

  const response = await api.get(url);

  return parseResponse(timeRecordAggregateResponseSchema, response.data, {
    schemaName: 'TimeRecordAggregate',
    endpoint: url,
  });
}
